// Appels à l'API depuis les fronts (même origine : Caddy en prod, le proxy de
// Vite en dev, renvoient /api vers le process de l'API).
import { t } from "./locale";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** Statut d'une requête qui n'a jamais atteint l'API (réseau coupé, serveur injoignable). */
export const NETWORK_ERROR = 0;

/**
 * Message d'un échec qui n'en porte pas (coupure, page d'erreur du relais,
 * corps vide…) : le même libellé partout où l'incident s'affiche.
 */
export function messageFor(status: number) {
  const copy = t().common.network;
  if (status === NETWORK_ERROR) return copy.offline;
  if (status === 401) return copy.expired;
  if (status === 403) return copy.forbidden;
  if (status === 404) return copy.notFound;
  if (status >= 500) return copy.unavailable;
  return copy.failed;
}

/** Une réponse 4xx ne changera pas en insistant ; une coupure ou un 5xx, peut-être. */
export function isTransient(error: unknown) {
  return !(error instanceof ApiError) || error.status === NETWORK_ERROR || error.status >= 500;
}

/** GET (ou autre méthode) JSON ; lève ApiError avec le message de l'API. */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: "same-origin",
      ...init,
      headers: init.body && typeof init.body === "string" ? { "Content-Type": "application/json", ...init.headers } : init.headers,
    });
  } catch (cause) {
    // Une annulation reste une annulation. Le reste est une coupure : le
    // message du navigateur (« Failed to fetch ») ne dit rien à l'utilisateur.
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(messageFor(NETWORK_ERROR), NETWORK_ERROR);
  }
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new ApiError(payload.error || messageFor(response.status), response.status);
  return payload;
}
